import { makeObservable, observable, action, computed } from 'mobx'
import axios from 'axios'
import { config } from '@/lib/config'

export interface WebChatMessage {
  id?: string
  room?: string
  guest?: string
  user?: string
  type?: string
  body: string
  image_url?: string
  created: string
}

interface WebChatConfig {
  tenant: string
  name: string
  enabled: boolean
}

interface WebChatSession {
  token: string
  guest?: { id: string; name: string }
  room?: { id: string }
  messages?: WebChatMessage[]
}

interface WSEvent {
  type: 'message' | 'room_created' | 'room_updated'
  room_id?: string
  message?: WebChatMessage
  room?: { id: string }
}

export type WebChatStatus = 'idle' | 'loading' | 'ready' | 'disabled' | 'error'

const POLLING_INTERVAL_MS = 5000
const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 30000

function tokenStorageKey(tenantId: string) {
  return `hoge_webchat_token:${tenantId}`
}

// Third-party iframe contexts may deny storage access, so every access must be
// defensive.
function readStoredToken(tenantId: string): string {
  try {
    return sessionStorage.getItem(tokenStorageKey(tenantId)) || ''
  } catch {
    return ''
  }
}

function writeStoredToken(tenantId: string, token: string) {
  try {
    sessionStorage.setItem(tokenStorageKey(tenantId), token)
  } catch {
    // ignore storage restrictions
  }
}

export class WebChatStore {
  tenantId = ''
  tenantName = ''
  status: WebChatStatus = 'idle'
  open = false

  token = ''
  guestId = ''
  guestName = ''
  roomId = ''

  messages: WebChatMessage[] = []
  input = ''
  sending = false
  wsStatus: 'disconnected' | 'connecting' | 'connected' = 'disconnected'

  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private pollTimer: ReturnType<typeof setInterval> | null = null

  constructor() {
    makeObservable(this, {
      tenantId: observable,
      tenantName: observable,
      status: observable,
      open: observable,
      token: observable,
      guestId: observable,
      guestName: observable,
      roomId: observable,
      messages: observable,
      input: observable,
      sending: observable,
      wsStatus: observable,
      isRealtimeEnabled: computed,
      toggle: action,
      openWidget: action,
      closeWidget: action,
      setInput: action,
      loadConfig: action,
      startSession: action,
      loadMessages: action,
      sendMessage: action,
      applyEvent: action,
    })
  }

  get isRealtimeEnabled() {
    return config.chatWsUrl !== ''
  }

  get apiBase() {
    return `${config.apiBaseUrl}/v1/webchat/${this.tenantId}`
  }

  async loadConfig() {
    this.status = 'loading'
    try {
      const response = await axios.get<WebChatConfig>(`${this.apiBase}/config`)
      this.tenantName = response.data.name || ''
      if (!response.data.enabled) {
        this.status = 'disabled'
        return false
      }
      this.status = 'ready'
      return true
    } catch (error) {
      console.error('Failed to load web chat config:', error)
      this.status = 'error'
      return false
    }
  }

  async startSession() {
    const stored = readStoredToken(this.tenantId)
    try {
      const response = await axios.post<WebChatSession>(`${this.apiBase}/session`, {
        token: stored,
        name: this.guestName || '',
      })

      this.token = response.data.token
      writeStoredToken(this.tenantId, this.token)

      if (response.data.guest) {
        this.guestId = response.data.guest.id
        this.guestName = response.data.guest.name || this.guestName
      }
      if (response.data.room) {
        this.roomId = response.data.room.id
      }
      this.messages = (response.data.messages || []) as WebChatMessage[]
      this.status = 'ready'
      return true
    } catch (error) {
      console.error('Failed to start web chat session:', error)
      this.status = 'error'
      return false
    }
  }

  async loadMessages() {
    if (!this.token) return
    try {
      const response = await axios.get<WebChatMessage[]>(`${this.apiBase}/messages`, {
        headers: { Authorization: `Bearer ${this.token}` },
      })
      this.messages = response.data || []
    } catch (error) {
      console.error('Failed to load web chat messages:', error)
    }
  }

  async sendMessage() {
    const text = this.input.trim()
    if (!text || this.sending) return

    this.sending = true
    try {
      const sent = this.wsStatus === 'connected' && this.sendViaWs(text)
      if (!sent) {
        const response = await axios.post<{ message?: WebChatMessage }>(
          `${this.apiBase}/messages`,
          { text },
          { headers: { Authorization: `Bearer ${this.token}` } }
        )
        if (response.data.message) {
          this.applyEvent({ type: 'message', room_id: this.roomId, message: response.data.message })
        }
      }
      this.input = ''
    } catch (error) {
      console.error('Failed to send web chat message:', error)
    } finally {
      this.sending = false
    }
  }

  private sendViaWs(text: string): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'message', data: { text } }))
      return true
    }
    return false
  }

  connectWs() {
    if (!this.isRealtimeEnabled || this.ws) return
    if (!this.tenantId || !this.roomId || !this.guestId) return

    this.wsStatus = 'connecting'
    const params = new URLSearchParams({
      x: 'guest',
      t: this.tenantId,
      r: this.roomId,
      g: this.guestId,
    })

    try {
      const ws = new WebSocket(`${config.chatWsUrl}?${params.toString()}`)
      this.ws = ws

      ws.onopen = () => {
        this.reconnectAttempts = 0
        this.wsStatus = 'connected'
      }
      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data as string) as WSEvent
          if (parsed && typeof parsed.type === 'string') {
            this.applyEvent(parsed)
          }
        } catch {
          // ignore malformed payloads
        }
      }
      ws.onclose = () => {
        if (this.ws === ws) {
          this.ws = null
          this.wsStatus = 'disconnected'
          this.scheduleReconnect()
        }
      }
      ws.onerror = () => {
        ws.close()
      }
    } catch (error) {
      console.error('Web chat WebSocket connection failed:', error)
      this.wsStatus = 'disconnected'
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect() {
    if (!this.isRealtimeEnabled || this.reconnectTimer || !this.open) return
    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts),
      RECONNECT_MAX_DELAY_MS
    )
    this.reconnectAttempts += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connectWs()
    }, delay)
  }

  startPolling() {
    if (this.pollTimer) return
    this.pollTimer = setInterval(() => {
      if (this.open && this.token) {
        void this.loadMessages()
      }
    }, POLLING_INTERVAL_MS)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  applyEvent(event: WSEvent) {
    if (event.type === 'message' && event.message) {
      if (event.room_id && this.roomId && event.room_id !== this.roomId) return
      if (!this.messages.some((m) => m.id === event.message!.id)) {
        this.messages = [...this.messages, event.message]
      }
    }
  }

  setInput(value: string) {
    this.input = value
  }

  openWidget() {
    this.open = true
    if (this.status === 'ready' && this.token) {
      this.connectWs()
      this.startPolling()
      return
    }
    if (this.status === 'loading') return

    void this.loadConfig().then(async (enabled) => {
      if (!enabled) return
      const ok = await this.startSession()
      if (ok) {
        this.connectWs()
        this.startPolling()
      }
    })
  }

  closeWidget() {
    this.open = false
  }

  toggle() {
    if (this.open) {
      this.closeWidget()
    } else {
      this.openWidget()
    }
  }

  disconnect() {
    this.stopPolling()
    this.reconnectAttempts = 0
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      const ws = this.ws
      this.ws = null
      ws.onclose = null
      ws.onerror = null
      ws.onmessage = null
      ws.close()
    }
    this.wsStatus = 'disconnected'
  }
}
